import { httpService } from '~/Services/Service';

const BASE = 'gestao-iptu';

// Web API espera arrays como `tiposMov=1&tiposMov=2` (repeat) — axios default usa
// `tiposMov[]=1` (com colchetes), que o model binder do .NET não popula em List<int>.
const paramsSerializer = (params) => {
  const parts = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== undefined && v !== null) {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
        }
      });
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });
  return parts.join('&');
};

export const filtrarImoveis = (params) =>
  httpService.get(`/${BASE}/filtrar`, { params, paramsSerializer });

export const lookupCarneStatus = () =>
  httpService.get(`/${BASE}/lookup/carne-status`);

export const lookupOpcoesIPTU = () =>
  httpService.get(`/${BASE}/lookup/iptu-opcoes`);

export const lookupFamilias = () =>
  httpService.get(`/${BASE}/lookup/familias`);

export const lookupGruposGe = () =>
  httpService.get(`/${BASE}/lookup/grupos-ge`);

// Lista de Codigo_Grupo_Empresa permitidas ao usuário em um Grupo GE específico.
// Réplica de UImovelCarne.pas:LGrupoGEChange — usada pra filtrar Empreendimento
// quando o usuário seleciona um Grupo GE no painel de filtros.
export const lookupGEsPorGrupoGe = (grupoGe) =>
  httpService.get(`/${BASE}/lookup/grupos-ge/${grupoGe}/grupos-empresa`);

// Melhoria filtros W-0718 — lookup de Cidades passa a depender de Grupo GE / GE.
// Sem nenhum dos dois → backend retorna lista vazia.
export const lookupCidades = (params) =>
  httpService.get(`/${BASE}/lookup/cidades`, { params });

// Melhoria filtros W-0718 — lookup das GEs (Grupo_Empresa) do usuário pro
// novo filtro "GE" do painel (mutuamente exclusivo com Grupo GE).
export const lookupGruposEmpresa = () =>
  httpService.get(`/${BASE}/lookup/grupos-empresa`);

export const lookupStatusImovel = () =>
  httpService.get(`/${BASE}/lookup/status-imovel`);

export const lookupStatusDebitoIPTU = () =>
  httpService.get(`/${BASE}/lookup/status-debito-iptu`);

export const alterarCarneStatus = (payload) =>
  httpService.post(`/${BASE}/alterar-carne-status`, payload);

export const alterarOpcaoPagamento = (payload) =>
  httpService.post(`/${BASE}/alterar-opcao-pagamento`, payload);

export const alterarOpcaoCondominio = (payload) =>
  httpService.post(`/${BASE}/alterar-opcao-condominio`, payload);

export const alterarCadPrefeitura = (payload) =>
  httpService.post(`/${BASE}/alterar-cad-prefeitura`, payload);

export const alterarOrdemPagamento = (payload) =>
  httpService.post(`/${BASE}/alterar-ordem-pagamento`, payload);

// D6 — POST com body (subs. GET com query). Aceita filtros + parâmetros do modal Excel.
// Retorna apenas o blob (interface antiga preservada para callers que não
// precisam do nome/MIME do servidor).
export const exportarExcel = (payload) =>
  httpService.post(`/${BASE}/exportar-excel`, payload, { responseType: 'blob' });

// D6 — variante que devolve { modo: 'unico' | 'multiplos', ... }.
//   - unico:     { blob, nomeArquivo, contentType }
//   - multiplos: { arquivos: [{ nomeArquivo, contentType, conteudoBase64 }] }
// Backend escolhe dinamicamente conforme ArquivoUnico (UImovelCarne.pas:725-731):
//   ArquivoUnico=true  → blob com .xlsx único
//   ArquivoUnico=false → JSON com lista de .xlsx por empreendimento (FE itera).
export const exportarExcelComMeta = async (payload) => {
  const res = await httpService.fetch({
    url: `/${BASE}/exportar-excel`,
    method: 'post',
    data: payload,
    responseType: 'blob',
    _returnFullResponse: true,
  });
  const contentType = (res.headers && res.headers['content-type']) || '';
  if (contentType.indexOf('application/json') >= 0) {
    // res.data é Blob (porque pedimos responseType:'blob'); convertemos para texto
    // e parseamos. Não dá pra mudar responseType após a request — Blob.text()
    // resolve isso. Backend serializa em snake_case (SnakeCaseNamingStrategy
    // global em Setpar.APIRest/Startup.cs), então normalizamos para camelCase
    // antes de devolver pro caller — assim o handler usa arq.nomeArquivo,
    // arq.contentType, arq.conteudoBase64 normalmente.
    const texto = await res.data.text();
    const json = JSON.parse(texto);
    const snakeToCamel = (s) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    const toCamelObj = (obj) => {
      if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
      if (Array.isArray(obj)) return obj.map(toCamelObj);
      const out = {};
      Object.keys(obj).forEach((k) => { out[snakeToCamel(k)] = toCamelObj(obj[k]); });
      return out;
    };
    const arquivos = (json.arquivos || []).map(toCamelObj);
    return { modo: 'multiplos', arquivos };
  }
  const cd = (res.headers && res.headers['content-disposition']) || '';
  const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(cd);
  const nomeArquivo = match ? match[1].replace(/['"]/g, '').trim() : null;
  return { modo: 'unico', blob: res.data, nomeArquivo, contentType };
};

// D6 — Polling de progresso (real, alimentado pelo backend a cada 10 linhas).
// Retorna { atual, total }. Quando id ainda não foi registrado ou já foi
// finalizado, backend responde {atual:0, total:0}.
export const consultarProgressoExcel = (progressId) =>
  httpService.get(`/${BASE}/exportar-excel/progresso/${progressId}`);

// D4 listagem read-only — espelha uDebitosIPTU.pas:FormShow (Q1).
// Chamada ao abrir o modal pra exibir o estado atual dos débitos
// (CHAVE LANÇADA / AGUARDANDO REMESSA / AGUARDANDO RETORNO / RETORNO OK)
// sem disparar JPlan nem gravar Remessa_Debito_IPTU.
export const listarDebitosIPTU = (payload) =>
  httpService.post(`/${BASE}/debitos-iptu/listar`, payload);

// D4 Fluxo 1 — Buscar Débitos IPTU (uDebitosIPTU.pas:btnBuscarDebitoClick).
// SCAFFOLDING: valida no backend e retorna lista vazia até HTTP JPlan estar
// implementado (Passo 2/4 do D4).
export const buscarDebitosIPTU = (payload) =>
  httpService.post(`/${BASE}/debitos-iptu/buscar`, payload);

// D4 Fluxo 2 — Preparar Lançamento (uDebitosIPTU.pas:btnLancarChaveClick).
// Backend monta a lista de despesas a revisar (RxDBGrid2 da uDebitosIPTU.dfm),
// guarda no LancamentoIPTUTracker e devolve { token, despesas, avisos }.
// Não grava nada em Contas_aPagar.
export const prepararLancamentoIPTU = (payload) =>
  httpService.post(`/${BASE}/debitos-iptu/preparar-lancamento`, payload);

// D4 Fluxo 3 — Efetivar Lançamento (uDebitosIPTU.pas:btnLancarClick).
// Backend lê o token do tracker e grava Contas_aPagar + CAP_Ativo dentro de
// UoW + RetryOnDeadlock por despesa. Retorna { lancados, avisos }.
export const efetivarLancamentoIPTU = (token) =>
  httpService.post(`/${BASE}/debitos-iptu/efetivar-lancamento`, { token });

// D7 — Pagamentos do Ativo (uIPTU_UltimaPaga.pas).
export const historicoPagamentosImovel = (codigoAtivo) =>
  httpService.get(`/${BASE}/imovel/${codigoAtivo}/historico-pagamentos`);

// W-0718 — Chaves Contas a Pagar do imóvel (modal Outras Informações).
// Lista ordenada por vencimento desc, projetando chave/vencimento/valor.
export const chavesContasPagarImovel = (codigoAtivo) =>
  httpService.get(`/${BASE}/imovel/${codigoAtivo}/chaves-contas-pagar`);

// D2 — Cálculo de Rescisão: lista contratos rescindidos do imóvel
// (uSelContrato.pas:FormShow). Usado quando MovTemRescisao='S'.
export const listarContratosRescisao = (codigoAtivo) =>
  httpService.get(`/${BASE}/calculo-rescisao/contratos/${codigoAtivo}`);

// D2 — Cálculo de Rescisão: dados pro relatório. Se boleto for null/omitido,
// o BE auto-resolve o último Boleto_RES (caminho `else` do Delphi quando
// MovTemRescisao<>'S').
export const obterCalculoRescisao = (codigoAtivo, codigoBoleto) =>
  httpService.get(`/${BASE}/calculo-rescisao/${codigoAtivo}`, {
    params: codigoBoleto ? { boleto: codigoBoleto } : {},
  });

// D2 Fase 2 — PDF do cálculo de rescisão (réplica do QuickReport UqrRES2.dfm).
// Retorna o arquivo binário direto pra download (Content-Disposition: attachment).
export const baixarCalculoRescisaoPdf = (codigoAtivo, codigoBoleto) =>
  httpService.get(`/${BASE}/calculo-rescisao/${codigoAtivo}/pdf`, {
    params: codigoBoleto ? { boleto: codigoBoleto } : {},
    responseType: 'blob',
  });

// D5 — Cad. Conta Prefeitura.
export const listarContasPrefeitura = (params) =>
  httpService.get(`/${BASE}/conta-prefeitura`, { params });

export const lookupContasPorGE = (ge) =>
  httpService.get(`/${BASE}/conta-prefeitura/lookup-contas`, { params: { ge } });

// D5 — GE (grupo_empresa) do usuário. Distinto de `lookupGruposGe` (tabela Grupo_GE).
export const lookupGePorUsuario = () =>
  httpService.get(`/${BASE}/conta-prefeitura/lookup-ge`);

export const incluirContaPrefeitura = (payload) =>
  httpService.post(`/${BASE}/conta-prefeitura/incluir`, payload);

export const excluirContaPrefeitura = (payload) =>
  httpService.post(`/${BASE}/conta-prefeitura/excluir`, payload);
